// Mode keys (design spec 2026-10-19 §2.1; decision 2026-10-19): `1`–`6` switch to Chat, Work,
// Chart, Cadence, Agents, Reports. Plain digits only: never while typing (inputs, textareas,
// selects, contenteditable editors), never with a modifier (Shift+digit types `!@#` and Alt+digit
// other characters; ⌘/Ctrl+digit belongs to browser tabs), and never while a dialog, menu or
// listbox owns the keyboard. Resolve's Shift+1–7 stays reserved for the desktop app.
import { useEffect, useRef } from "react";

export type ModeKeyEvent = Pick<
  KeyboardEvent,
  "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey" | "defaultPrevented" | "repeat" | "target"
>;

export function isTyping(target: EventTarget | null): boolean {
  if (typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target.closest("[contenteditable='true']")) return true;
  return (
    /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.getAttribute("role") === "textbox"
  );
}

/** A modal, menu or listbox owns the keyboard while it is open. */
function overlayOpen(): boolean {
  return (
    typeof document !== "undefined" &&
    document.querySelector(
      '[role="dialog"][aria-modal="true"], [role="alertdialog"], [role="menu"], [role="listbox"]',
    ) !== null
  );
}

/** The digit a key press switches to (`"1"`–`"7"`), or null when it must be left alone. */
export function modeDigit(event: ModeKeyEvent): string | null {
  if (event.defaultPrevented || event.repeat) return null;
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return null;
  if (!/^[1-7]$/.test(event.key)) return null;
  if (isTyping(event.target) || overlayOpen()) return null;
  return event.key;
}

export type ModeKey = { digit: string; run: () => void };

/** Binds the digits on `window`; off while `enabled` is false (e.g. presenting). */
export function useModeKeys(keys: readonly ModeKey[], enabled = true): void {
  const latest = useRef(keys);
  latest.current = keys;
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const onKeyDown = (event: KeyboardEvent) => {
      const digit = modeDigit(event);
      if (!digit) return;
      const hit = latest.current.find((k) => k.digit === digit);
      if (!hit) return;
      event.preventDefault();
      hit.run();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
